
import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ProfileSettings } from '@/components/admin/settings/ProfileSettings';
import { NotificationSettings } from '@/components/admin/settings/NotificationSettings';
import { SystemSettings } from '@/components/admin/settings/SystemSettings';
import { ComplianceSettings } from '@/components/admin/settings/ComplianceSettings';
import { ExportSettings } from '@/components/admin/settings/ExportSettings';
import { IntegrationSettings } from '@/components/admin/settings/IntegrationSettings';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useToast } from '@/hooks/use-toast';
import { Shield, Bell, Settings as SettingsIcon, FileText, Download, Link, Mail } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function Settings() {
  const { adminUser } = useAdminAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('profile');

  // Parse the activeTab from the URL query params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    if (tabParam && ['profile', 'notifications', 'system', 'compliance', 'export', 'integrations', 'email-templates'].includes(tabParam)) {
      // Redirect email-templates tab to the dedicated page
      if (tabParam === 'email-templates') {
        navigate('/email-templates');
        return;
      }
      setActiveTab(tabParam);
    }
  }, [location.search, navigate]);

  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    if (value === 'email-templates') {
      navigate('/email-templates');
      return;
    }
    
    setActiveTab(value);
    navigate(`/settings?tab=${value}`, { replace: true });
  };

  // Simple check if settings were saved (for demonstration)
  const handleSettingsSaved = (section: string) => {
    toast({
      title: 'Settings Saved',
      description: `Your ${section} settings have been saved successfully.`,
    });
  };

  // Handle navigation to Email Templates page
  const handleNavigateToEmailTemplates = () => {
    navigate('/email-templates');
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Settings</h1>
            <p className="text-muted-foreground mt-1">
              Configure your admin profile and system preferences
            </p>
          </div>
        </div>

        <Card>
          <CardContent className="p-0">
            <Tabs 
              defaultValue={activeTab} 
              value={activeTab}
              onValueChange={handleTabChange} 
              className="w-full"
            >
              <div className="flex border-b overflow-x-auto">
                <TabsList className="h-auto p-0 bg-transparent flex-wrap">
                  <TabsTrigger 
                    value="profile"
                    className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-4 py-3 data-[state=active]:shadow-none"
                  >
                    <div className="flex items-center gap-2">
                      <SettingsIcon className="h-4 w-4" />
                      <span>Admin Profile</span>
                    </div>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="notifications"
                    className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-4 py-3 data-[state=active]:shadow-none"
                  >
                    <div className="flex items-center gap-2">
                      <Bell className="h-4 w-4" />
                      <span>Notifications</span>
                    </div>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="system"
                    className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-4 py-3 data-[state=active]:shadow-none"
                  >
                    <div className="flex items-center gap-2">
                      <SettingsIcon className="h-4 w-4" />
                      <span>System</span>
                    </div>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="compliance"
                    className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-4 py-3 data-[state=active]:shadow-none"
                  >
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      <span>Compliance</span>
                    </div>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="export"
                    className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-4 py-3 data-[state=active]:shadow-none"
                  >
                    <div className="flex items-center gap-2">
                      <Download className="h-4 w-4" />
                      <span>Export & Data</span>
                    </div>
                  </TabsTrigger>
                  <TabsTrigger 
                    value="integrations"
                    className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none px-4 py-3 data-[state=active]:shadow-none"
                  >
                    <div className="flex items-center gap-2">
                      <Link className="h-4 w-4" />
                      <span>Integrations</span>
                    </div>
                  </TabsTrigger>
                  {/* Email Templates button that navigates to dedicated page */}
                  <Button
                    variant="ghost"
                    onClick={handleNavigateToEmailTemplates}
                    className="flex items-center gap-2 px-4 py-3 rounded-none hover:bg-accent"
                  >
                    <Mail className="h-4 w-4" />
                    <span>Email Templates</span>
                  </Button>
                </TabsList>
              </div>
            
              <div className="p-6">
                <TabsContent value="profile" className="mt-0">
                  <ProfileSettings adminUser={adminUser} onSave={() => handleSettingsSaved('profile')} />
                </TabsContent>
                
                <TabsContent value="notifications" className="mt-0">
                  <NotificationSettings onSave={() => handleSettingsSaved('notification')} />
                </TabsContent>
                
                <TabsContent value="system" className="mt-0">
                  <SystemSettings onSave={() => handleSettingsSaved('system')} />
                </TabsContent>
                
                <TabsContent value="compliance" className="mt-0">
                  <ComplianceSettings onSave={() => handleSettingsSaved('compliance')} />
                </TabsContent>
                
                <TabsContent value="export" className="mt-0">
                  <ExportSettings onSave={() => handleSettingsSaved('export')} />
                </TabsContent>
                
                <TabsContent value="integrations" className="mt-0">
                  <IntegrationSettings onSave={() => handleSettingsSaved('integration')} />
                </TabsContent>
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
